import { z } from "zod";

// Wire shapes for the policy-detail dialogs (Endorsements, Related Policies,
// Locations / Class Codes, Service Team, Employer Contact). Each dialog used
// to define these inline alongside its component; consolidating keeps the
// API response contracts in one place and out of presentation files.

// ── EndorsementsDialog ──

export const formEntrySchema = z.object({
  id: z.string(),
  formNumber: z.string(),
  formName: z.string(),
  formEdition: z.string(),
  effectiveDate: z.string(),
});
export const formEntriesSchema = z.array(formEntrySchema);
export type FormEntry = z.infer<typeof formEntrySchema>;

// ── RelatedPoliciesDialog ──

export const relatedPolicySchema = z.object({
  policyNumber: z.string(),
  feinTaxId: z.string(),
  insuredName: z.string(),
});
export const relatedPoliciesSchema = z.array(relatedPolicySchema);
export type RelatedPolicy = z.infer<typeof relatedPolicySchema>;

// ── LocationsClassCodesDialog ──

export const locationSchema = z.object({
  id: z.string(),
  locationNumber: z.string(),
  locationName: z.string().nullable(),
  locationAddress1: z.string().nullable(),
  locationCity: z.string().nullable(),
  locationState: z.string().nullable(),
  locationZipCode: z.string().nullable(),
});
export const classCodeSchema = z.object({
  id: z.string(),
  locationNumber: z.string(),
  classCode: z.string(),
  classDescription: z.string().nullable(),
  exposure: z.number().nullable(),
  rate: z.number().nullable(),
  manualPremium: z.number().nullable(),
});
export const locationsDataSchema = z.object({
  locations: z.array(locationSchema),
  classCodes: z.array(classCodeSchema),
});
export type Location = z.infer<typeof locationSchema>;
export type ClassCode = z.infer<typeof classCodeSchema>;

// ── ServiceTeamDialog ──

export const underwritingTeamSchema = z.object({
  policyNumber: z.string(),
  effectiveDate: z.string(),
  agentNumber: z.string().nullable(),
  underwriterName: z.string().nullable(),
  accountAnalystName: z.string().nullable(),
  policyIssuanceCoordinatorName: z.string().nullable(),
  billingAnalystName: z.string().nullable(),
  lossControlConsultantName: z.string().nullable(),
});
export const claimTeamMemberSchema = z.object({
  id: z.string(),
  region: z.string().nullable(),
  jurisdictionState: z.string().nullable(),
  uwCompany: z.string().nullable(),
  claimAdjusterName: z.string().nullable(),
  claimTeamManagerName: z.string().nullable(),
});
export const serviceTeamsDataSchema = z.object({
  underwriting: underwritingTeamSchema.nullable(),
  claims: z.array(claimTeamMemberSchema),
});
export type UnderwritingTeam = z.infer<typeof underwritingTeamSchema>;
export type ClaimTeamMember = z.infer<typeof claimTeamMemberSchema>;
export type ServiceTeamsData = z.infer<typeof serviceTeamsDataSchema>;

// ── EmployerContactDialog ──

export const employerContactSchema = z.object({
  id: z.string(),
  policyNumber: z.string(),
  fullName: z.string(),
  jobTitle: z.string().nullable(),
  emailAddress: z.string().nullable(),
});
export const employerContactsSchema = z.array(employerContactSchema);
export type EmployerContact = z.infer<typeof employerContactSchema>;
